import * as React from 'react';
import { createContext, useContext } from 'react';

import { useLayer } from '@coursera-web/statsig';

import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';

/** Variant groups configured on the `campaigns-dd4e5efb` experiment (MERCH-3512). */
export const AI_SERIES_CONTROL_VARIANT = 'control';
export const AI_SERIES_TEST_VARIANT = 'B';

/** The campaign whose modules, when shown on LOHP, LIHP or RDP, switch the AI Series modules on. */
export const AI_SERIES_CAMPAIGN_ID = '7fb18d5f-289a-4423-94c7-aa44e2477c24';

type AiSeriesContextType = {
  /** Whether this learner is in the AI Series treatment group. */
  showAiSeries: boolean;
};

const AiSeriesContext = createContext<AiSeriesContextType>({
  showAiSeries: false,
});

export const useAiSeriesContext = () => useContext(AiSeriesContext);

/**
 * Whether the AI Series experience should be shown to this learner.
 *
 * Returns `false` outside `AiSeriesProvider`, so an unwrapped tree degrades to the
 * control experience rather than throwing.
 */
export const useShowAiSeries = (): boolean => useAiSeriesContext().showAiSeries;

/**
 * Reads the AI Series assignment straight from the `campaigns-dd4e5efb` layer, without
 * needing a provider above it.
 *
 * Prefer `useShowAiSeries` when several components in a subtree need the assignment —
 * the provider reads the layer once for all of them. Reach for this hook when a single
 * component needs it, as on the logged-out home page.
 *
 * When a surface can only show the experience to some of its visitors — e.g. the role
 * description page, where only a few roles map to a series — pass `enabled: false` for
 * the rest so they are not exposed.
 *
 * Requires a `StatsigProvider` above it in the tree.
 */
export const useIsAiSeriesTreatment = ({ enabled = true }: { enabled?: boolean } = {}): boolean => {
  const layer = useLayer('campaigns-dd4e5efb');

  return enabled && layer.get('variant', AI_SERIES_CONTROL_VARIANT) === AI_SERIES_TEST_VARIANT;
};

/**
 * Whether a module from the AI Series campaign, with non-null content, is part of the
 * campaign data on this page.
 *
 * Requires a `CampaignModulesDataContext` provider above it; without one it returns `false`.
 * Pass `enabled: false` to skip the check for visitors who could never see the experience.
 */
export const useIsAiSeriesCampaignActive = ({ enabled = true }: { enabled?: boolean } = {}): boolean => {
  const { campaignData } = useCampaignModulesData();

  return (
    enabled &&
    Object.values(campaignData ?? {}).some(
      (module) => module?.matchedCampaign?.campaignId === AI_SERIES_CAMPAIGN_ID && module.contentDataJson !== null
    )
  );
};

type Props = {
  children: React.ReactNode;
};

/**
 * Provides whether the AI Series modules should show, based on the AI Series campaign
 * being among the campaign modules on the page.
 *
 * Requires a `CampaignModulesDataContext` provider above it in the tree.
 */
const AiSeriesProvider = ({ children }: Props) => {
  const showAiSeries = useIsAiSeriesCampaignActive();

  const value = React.useMemo(() => ({ showAiSeries }), [showAiSeries]);

  return <AiSeriesContext.Provider value={value}>{children}</AiSeriesContext.Provider>;
};

export default AiSeriesProvider;
